import { useState } from 'react';
import { ExpenseCategory, ExpenseTransaction } from '../../types';
import { db } from '../../db';

interface SyncQueueItem {
  type: string;
  action: string;
  data: unknown;
  status: string;
  timestamp: number;
}

export function useExpenses(
  addToSyncQueue: (item: SyncQueueItem) => Promise<unknown>,
  assertMutationAllowed: (fnName: string) => void
) {
  const [expenseCategories, setExpenseCategories] = useState<ExpenseCategory[]>([]);
  const [expenses, setExpenses] = useState<ExpenseTransaction[]>([]);

  const addExpenseCategory = async (item: ExpenseCategory) => {
    const id = await db.expenseCategories.add(item);
    const newItem = { ...item, id: id as number };
    await db.syncStack.add({ type: 'expenseCategory', action: 'create', data: newItem, status: 'pending', timestamp: Date.now() });
    setExpenseCategories(prev => [...prev, newItem]);
  };

  const updateExpenseCategory = async (item: ExpenseCategory) => {
    assertMutationAllowed('updateExpenseCategory');
    await db.expenseCategories.put(item);
    await addToSyncQueue({ type: 'expenseCategory', action: 'update', data: item, status: 'pending', timestamp: Date.now() });
    setExpenseCategories(prev => prev.map(i => i.id === item.id ? item : i));
  };

  const deleteExpenseCategory = async (id: number) => {
    await db.expenseCategories.delete(id);
    await db.syncStack.add({ type: 'expenseCategory', action: 'delete', data: { id }, status: 'pending', timestamp: Date.now() });
    setExpenseCategories(prev => prev.filter(i => i.id !== id));
  };

  const addExpense = async (item: ExpenseTransaction) => {
    assertMutationAllowed('addExpense');
    const id = await db.expenses.add(item);
    const newItem = { ...item, id: id as number };
    await db.syncStack.add({ type: 'expense', action: 'create', data: newItem, status: 'pending', timestamp: Date.now() });
    setExpenses(prev => [{ ...item, id: id as number }, ...prev]);
  };

  const deleteExpense = async (id: number) => {
    await db.expenses.delete(id);
    await db.syncStack.add({ type: 'expense', action: 'delete', data: { id }, status: 'pending', timestamp: Date.now() });
    setExpenses(prev => prev.filter(i => i.id !== id));
  };

  return {
    expenseCategories,
    setExpenseCategories,
    expenses,
    setExpenses,
    addExpenseCategory,
    updateExpenseCategory,
    deleteExpenseCategory,
    addExpense,
    deleteExpense,
  };
}
